import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ReaderView } from '@/types/api';

/**
 * ReadersStore caches the authenticated user's readers and tracks which
 * profile is "active" — the one the header pill + bookmark button + Library
 * personalization all key off of.
 *
 * We persist both `readers` and `selectedReaderId` so the UI renders the
 * right reader context on a cold reload before the readers refetch
 * resolves.
 */
export interface ReadersState {
  readers: ReaderView[];
  selectedReaderId: string | null;
  /**
   * Per-reader coin balance cache, keyed by reader id. `null` means
   * "not yet hydrated from the server" — distinct from `0`, which is a
   * valid balance. Kept as a plain object so the entry is retained
   * across reader switches (avoids a flash-to-zero when the header chip
   * re-reads a reader it's already seen).
   */
  coinBalances: Record<string, number | null>;
  setReaders: (readers: ReaderView[]) => void;
  upsertReader: (reader: ReaderView) => void;
  removeReader: (id: string) => void;
  setSelectedReaderId: (id: string | null) => void;
  selectedReader: () => ReaderView | null;
  /** Overwrite the cached balance for a reader (used after a fresh GET). */
  setCoinBalance: (readerId: string, balance: number) => void;
  /**
   * Apply an optimistic delta to the cached balance. No-ops when the
   * reader's balance hasn't been hydrated yet — the next server read
   * will pull the canonical value, so we don't invent one from a delta
   * alone.
   */
  incrementCoinBalance: (readerId: string, delta: number) => void;
  /** Convenience reader for the currently-selected reader's balance. */
  selectedCoinBalance: () => number | null;
  reset: () => void;
}

export const useReadersStore = create<ReadersState>()(
  persist(
    (set, get) => ({
      readers: [],
      selectedReaderId: null,
      coinBalances: {},
      setReaders: (readers) => {
        set({ readers });
        // Auto-select the first reader if nothing is selected, or clear the
        // selection if the currently-selected reader no longer exists.
        const { selectedReaderId } = get();
        if (!selectedReaderId && readers.length > 0) {
          set({ selectedReaderId: readers[0]!.id });
        } else if (selectedReaderId && !readers.some((reader) => reader.id === selectedReaderId)) {
          set({ selectedReaderId: readers[0]?.id ?? null });
        }
      },
      upsertReader: (reader) => {
        const existing = get().readers;
        const next = existing.some((r) => r.id === reader.id)
          ? existing.map((r) => (r.id === reader.id ? reader : r))
          : [...existing, reader];
        set({ readers: next });
      },
      removeReader: (id) => {
        const next = get().readers.filter((r) => r.id !== id);
        const { [id]: _discard, ...remainingBalances } = get().coinBalances;
        set({ readers: next, coinBalances: remainingBalances });
        if (get().selectedReaderId === id) {
          set({ selectedReaderId: next[0]?.id ?? null });
        }
      },
      setSelectedReaderId: (selectedReaderId) => set({ selectedReaderId }),
      selectedReader: () => {
        const { readers, selectedReaderId } = get();
        return readers.find((r) => r.id === selectedReaderId) ?? null;
      },
      setCoinBalance: (readerId, balance) => {
        set({ coinBalances: { ...get().coinBalances, [readerId]: balance } });
      },
      incrementCoinBalance: (readerId, delta) => {
        const current = get().coinBalances[readerId];
        // Only mutate if we already have a canonical value; otherwise a
        // stale "delta from nothing" would poison the cache until the
        // next hydration.
        if (current === null || current === undefined) return;
        set({
          coinBalances: { ...get().coinBalances, [readerId]: current + delta },
        });
      },
      selectedCoinBalance: () => {
        const { coinBalances, selectedReaderId } = get();
        if (!selectedReaderId) return null;
        return coinBalances[selectedReaderId] ?? null;
      },
      reset: () => set({ readers: [], selectedReaderId: null, coinBalances: {} }),
    }),
    {
      name: 'readers-store',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
